// Shared JSON-LD builders for the tenant-facing site. Kept data-driven off
// the tenant's brand blob so this works for any Foundation Signature
// tenant, not just the one it was first written for.

function parseAddress(addressLine) {
  // "Chertsey Road, Windlesham, Surrey, GU20 6HP" -> best-effort parts.
  // Falls back gracefully if the format differs — schema.org tolerates
  // a plain streetAddress-only PostalAddress just fine.
  if (!addressLine) return null;
  const parts = addressLine.split(",").map((p) => p.trim()).filter(Boolean);
  const postcode = parts.find((p) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(p));
  const rest = parts.filter((p) => p !== postcode);
  return {
    "@type": "PostalAddress",
    streetAddress: rest[0] || addressLine,
    addressLocality: rest[1] || "",
    addressRegion: rest[2] || "",
    postalCode: postcode || "",
    addressCountry: "GB",
  };
}

// Very light parse of "Monday – Friday: 8:30am – 5:00pm\nSaturday: ..." into
// schema.org OpeningHoursSpecification. Only handles clean HH:MMam/pm ranges
// on weekday spans — anything it can't confidently parse (e.g. "by
// appointment only") is simply skipped rather than guessed at.
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
function parseOpeningHours(text) {
  if (!text) return [];
  const to24h = (t) => {
    const m = t.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2] || "00";
    const mer = m[3].toLowerCase();
    if (mer === "pm" && h !== 12) h += 12;
    if (mer === "am" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${min}`;
  };
  const specs = [];
  for (const line of text.split("\n")) {
    const [dayPart, hoursPart] = line.split(":").map((s) => s?.trim());
    if (!dayPart || !hoursPart) continue;
    const rangeMatch = hoursPart.match(/^([\d:apm\s]+)\s*[–-]\s*([\d:apm\s]+)$/i);
    if (!rangeMatch) continue;
    const opens = to24h(rangeMatch[1]);
    const closes = to24h(rangeMatch[2]);
    if (!opens || !closes) continue;
    let days = [];
    if (dayPart.includes("–") || dayPart.includes("-")) {
      const [start, end] = dayPart.split(/[–-]/).map((s) => s.trim());
      const si = DAY_NAMES.findIndex((d) => d.startsWith(start));
      const ei = DAY_NAMES.findIndex((d) => d.startsWith(end));
      if (si >= 0 && ei >= si) days = DAY_NAMES.slice(si, ei + 1);
    } else {
      const di = DAY_NAMES.findIndex((d) => d.startsWith(dayPart));
      if (di >= 0) days = [DAY_NAMES[di]];
    }
    if (days.length) {
      specs.push({ "@type": "OpeningHoursSpecification", dayOfWeek: days, opens, closes });
    }
  }
  return specs;
}

export function buildLocalBusinessJsonLd(brand, { url, imageUrl } = {}) {
  const biz = brand.business || {};
  if (!biz.name) return null;

  const testimonials = brand.testimonials || [];
  const reviews = testimonials.slice(0, 5).map((t) => ({
    "@type": "Review",
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    author: { "@type": "Person", name: t.author },
    reviewBody: t.quote,
  }));

  const ld = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: biz.name,
    description: biz.elevator_pitch || biz.hero_sub || undefined,
    url: url || biz.website_url || undefined,
    telephone: biz.phone || undefined,
    email: biz.email || undefined,
    image: imageUrl || undefined,
    address: parseAddress(biz.address) || undefined,
    areaServed: (brand.areas_served || []).map((a) => ({ "@type": "Place", name: a })),
    priceRange: "££",
  };

  const hours = parseOpeningHours(biz.opening_hours);
  if (hours.length) ld.openingHoursSpecification = hours;

  if (reviews.length) {
    ld.review = reviews;
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: "5",
      reviewCount: String(testimonials.length),
    };
  }

  // Strip undefined keys — schema.org validators are picky about literal "undefined"
  return JSON.parse(JSON.stringify(ld));
}

export function buildPropertyJsonLd(prop, brand, { url, resolveImageUrl = (u) => u } = {}) {
  if (!prop) return null;
  const biz = brand.business || {};
  const isRent = prop.listing_type === "letting";
  const price = isRent ? prop.rental_pcm : prop.asking_price;

  const ld = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    url: url || undefined,
    name: `${prop.address_line1}, ${prop.town || ""}`.trim(),
    description: prop.description || undefined,
    datePosted: prop.updated_at || undefined,
    image: (prop.images || []).map((i) => resolveImageUrl(i.url)).filter(Boolean),
    about: {
      "@type": "Residence",
      name: prop.address_line1,
      address: {
        "@type": "PostalAddress",
        streetAddress: prop.address_line1,
        addressLocality: prop.town || "",
        postalCode: prop.postcode || "",
        addressCountry: "GB",
      },
      numberOfRooms: prop.bedrooms || undefined,
      numberOfBathroomsTotal: prop.bathrooms || undefined,
      floorSize: prop.size_sqft
        ? { "@type": "QuantitativeValue", value: prop.size_sqft, unitCode: "FTK" }
        : undefined,
    },
    offers: {
      "@type": "Offer",
      price: price || undefined,
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      businessFunction: isRent ? "http://purl.org/goodrelations/v1#LeaseOut" : "http://purl.org/goodrelations/v1#Sell",
      seller: biz.name ? { "@type": "RealEstateAgent", name: biz.name } : undefined,
    },
  };
  return JSON.parse(JSON.stringify(ld));
}

export function buildFaqJsonLd(faqItems) {
  if (!faqItems || !faqItems.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

export function buildBreadcrumbJsonLd(items) {
  // items: [{ name, url }] in order from home to current page
  if (!items || items.length < 2) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function formatArticleDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function estimateReadingTime(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

/**
 * Foundation's own schema.org blocks for its marketing pages, shared by the
 * home and pricing pages. backend/prerender.py publishes the same blocks to
 * crawlers (_foundation_org_jsonld, _foundation_offers_jsonld), so keep the
 * two in step.
 *
 * "Foundation" alone is too common a name for a search engine to resolve to
 * us, hence the alternate names and the two records nobody else can claim.
 */
const FDN_ORIGIN = "https://foundationagent.co.uk";
export const FOUNDATION_ALT_NAMES = ["Foundation Agent", "Foundation Estate Agent CRM"];
export const FOUNDATION_SAME_AS = [
  "https://apps.apple.com/gb/app/foundation-estate-agent-crm/id6802866527",
  "https://find-and-update.company-information.service.gov.uk/company/17431696",
];

const FDN_PLAN_OFFERS = [
  { name: "Foundation Essentials", price: "29", description: "Valuations, buyer register and the full calculator suite. Ready today." },
  { name: "Foundation Social", price: "29", description: "Two branded social posts every day. One weekly market report. Zero effort." },
  { name: "Foundation Growth", price: "49", description: "Everything in Essentials, plus the CRM. Run sales and lettings day to day, with your listings on your own website." },
  { name: "Foundation Signature", price: "89", description: "The complete platform. Everything, unlocked." },
];

// Every plan as its own monthly Offer, so "how much is Growth?" has an answer
// a crawler can read rather than only a 29 to 89 range.
export function buildFoundationOffersJsonLd() {
  const prices = FDN_PLAN_OFFERS.map((o) => Number(o.price));
  return {
    "@type": "AggregateOffer",
    priceCurrency: "GBP",
    lowPrice: String(Math.min(...prices)),
    highPrice: String(Math.max(...prices)),
    offerCount: String(FDN_PLAN_OFFERS.length),
    url: `${FDN_ORIGIN}/pricing`,
    offers: FDN_PLAN_OFFERS.map((o) => ({
      "@type": "Offer",
      name: o.name,
      description: o.description,
      price: o.price,
      priceCurrency: "GBP",
      url: `${FDN_ORIGIN}/pricing`,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: o.price,
        priceCurrency: "GBP",
        unitText: "MONTH",
        billingDuration: "P1M",
      },
    })),
  };
}
